// Re-tier by declared CDT order, not weight; keep each child value's shelf range contiguous.
import { GeneratedLayout, StudioItem, StudioRunGeometry } from "../studioTypes";
import { LaneSegment, dealAcrossBand, laneSegmentsOf, shareShelves } from "./layoutOps";

export interface RebuildRequest {
  /** Block keys in the declared (CDT) order their bands stack in. */
  group: readonly string[];
  blockKeyOf: (skuId: string) => string;
  run: StudioRunGeometry;
  /** The child attribute each block re-tiers by, or null to level whole. */
  childAttrOf: (blockKey: string) => string | null;
}

/** Mutates `layout` (a clone). Returns the shelf bands assigned, or null when the group has no product in the run. */
export const rebuildColumn = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  attrsOf: (skuId: string) => Record<string, string | null>,
  request: RebuildRequest,
): Map<string, number[]> | null => {
  const { group, blockKeyOf, run, childAttrOf } = request;
  const ranks = [...run.shelves].map((shelf) => shelf.rank).sort((a, b) => a - b);
  const heights = new Map(items.map((item) => [item.skuId, item.heightCm || 0]));

  const perBlock = new Map<string, LaneSegment[]>();
  for (const lane of laneSegmentsOf(layout, items).values()) {
    for (const entry of lane) {
      if (entry.segment.run !== run.run) continue;
      const key = blockKeyOf(entry.skuId);
      if (!group.includes(key)) continue;
      const list = perBlock.get(key) ?? [];
      list.push(entry);
      perBlock.set(key, list);
    }
  }
  const present = group.filter((key) => (perBlock.get(key)?.length ?? 0) > 0);
  if (present.length === 0) return null;

  const widths = present.map((key) =>
    (perBlock.get(key) ?? []).reduce((sum, entry) => sum + entry.widthCm, 0),
  );
  const share = shareShelves(widths, ranks.length);
  const bands = new Map<string, number[]>();
  let offset = 0;
  present.forEach((key, index) => {
    const band = ranks.slice(offset, offset + share[index]);
    offset += share[index];
    bands.set(key, band.length > 0 ? band : [ranks[ranks.length - 1]]);
  });

  for (const key of present) {
    const band = bands.get(key) ?? [];
    const segments = perBlock.get(key) ?? [];
    const childAttr = childAttrOf(key);
    if (!childAttr) {
      dealAcrossBand(segments, band, run, (skuId) => heights.get(skuId) ?? 0);
      continue;
    }
    // Child bands: children in current shelf order, each a contiguous slice.
    const byChild = new Map<string, LaneSegment[]>();
    for (const entry of segments) {
      const child = String(attrsOf(entry.skuId)[childAttr] ?? "");
      const list = byChild.get(child) ?? [];
      list.push(entry);
      byChild.set(child, list);
    }
    if (byChild.size < 2) {
      dealAcrossBand(segments, band, run, (skuId) => heights.get(skuId) ?? 0);
      continue;
    }
    const children = [...byChild.entries()].sort((a, b) => {
      const meanShelf = (list: LaneSegment[]) =>
        list.reduce((sum, entry) => sum + entry.segment.shelf, 0) / list.length;
      return meanShelf(a[1]) - meanShelf(b[1]);
    });
    const childShare = shareShelves(
      children.map(([, list]) => list.reduce((sum, entry) => sum + entry.widthCm, 0)),
      band.length,
    );
    let childOffset = 0;
    children.forEach(([, list], index) => {
      const childBand = band.slice(childOffset, childOffset + childShare[index]);
      childOffset += childShare[index];
      dealAcrossBand(
        list,
        childBand.length > 0 ? childBand : [band[band.length - 1]],
        run,
        (skuId) => heights.get(skuId) ?? 0,
      );
    });
  }
  return bands;
};
